import React, { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from 'contexts'
import { useInventorySettingsMutations, useAutoOrderMutations } from 'hooks/api'
import { usePaymentMethods } from 'hooks/api/usePaymentMethods'
import type { PaymentMethodRow } from 'hooks/api/usePaymentMethods'
import { useCanAccess } from 'hooks'

import {
  Stack,
  Typography,
  Collapse,
  Divider,
  Tooltip,
  Select as MuiSelect,
  MenuItem,
} from '@mui/material'
import { CheckIcon } from 'lucide-react'
import { Button, Modal, MenuButton, ToggleButton, Input, Switch } from '@repo/ui'
import { PaymentCell } from 'components'

interface SetInventorySettingsModalProps {
  open: boolean
  onClose: () => void
  refetch: () => void
  inventory: any
}

// A valid threshold is a non-negative integer; percentage additionally caps at 100.
const validateThreshold = (raw: string, type: string): string | null => {
  const value = (raw ?? '').trim()
  if (value === '') return 'Enter a threshold'
  if (!/^\d+$/.test(value)) return 'Numbers only'
  if (type === 'percentage' && Number(value) > 100) return 'Max 100%'
  return null
}

/**
 * Inventory settings — one modal, two sections keyed off the shared low-stock
 * threshold at the top (VERA-246):
 *   • Low-stock alert threshold — the threshold + email alert (tags_inventory_settings).
 *   • Automatic reorder — the money surface (tags_auto_order); disabled + tooltip
 *     when the user lacks that permission. Both persist via their own endpoints,
 *     so the alert save never touches the auto-order card and vice-versa.
 */
export const SetInventorySettingsModal = (props: SetInventorySettingsModalProps) => {
  const { open, onClose = () => {}, refetch, inventory } = props
  const { open: notification } = useSnackbar()

  const { isHasAccess: canAutoOrder } = useCanAccess({ action: 'tags_auto_order' })

  // ── Alert-threshold state ─────────────────────────────────────────────────
  const [tab, setTab] = useState<'number' | 'percentage'>(
    inventory.threshold_type === 'percentage' ? 'percentage' : 'number',
  )
  const [sendEmail, setSendEmail] = useState<boolean>(inventory.send_threshold_email)
  const [threshold, setThreshold] = useState(String(inventory.threshold ?? 0))

  // ── Auto-reorder state ────────────────────────────────────────────────────
  const variants = inventory.tag_shop_product?.variants ?? []
  const [autoOrder, setAutoOrder] = useState<boolean>(inventory.auto_order)
  const [autoOrderVariant, setAutoOrderVariant] = useState(inventory.auto_order_variant)
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(
    inventory.auto_order_payment_method_id ?? null,
  )

  const { data: pmData, isLoading: isLoadingCards } = usePaymentMethods()
  const cards = useMemo<PaymentMethodRow[]>(
    () =>
      (pmData?.methods ?? []).map((method) => ({
        ...method,
        type: method.brand?.toLowerCase(),
        label: `${(method.brand || 'Card').toUpperCase()} •••• ${method.last4}`.trim(),
        default: method.isDefault,
        details: { card_number: `**** **** **** ${method.last4}` },
      })) as PaymentMethodRow[],
    [pmData?.methods],
  )
  const defaultCardId = useMemo(
    () => cards.find((c) => c.isDefault)?.id ?? cards[0]?.id ?? null,
    [cards],
  )
  // Preselect the default card once the list loads, when nothing is persisted yet.
  useEffect(() => {
    if (open && !paymentMethodId && defaultCardId) setPaymentMethodId(defaultCardId)
  }, [open, paymentMethodId, defaultCardId])

  // ── Mutations (one per surface; each hits its own permission-gated endpoint) ─
  const { updateInventorySettingsAsync, isSaving: isSavingSettings } =
    useInventorySettingsMutations()
  const { updateAutoOrderAsync, isSaving: isSavingAuto } = useAutoOrderMutations()
  const isSaving = isSavingSettings || isSavingAuto

  const thresholdError = validateThreshold(threshold, tab)

  const alertChanged =
    Number(inventory.threshold ?? 0) !== Number(threshold) ||
    (inventory.threshold_type ?? 'number') !== tab ||
    inventory.send_threshold_email !== sendEmail
  const autoChanged =
    canAutoOrder &&
    (inventory.auto_order !== autoOrder ||
      inventory.auto_order_variant?.quantity !== autoOrderVariant?.quantity ||
      (inventory.auto_order_payment_method_id ?? null) !== paymentMethodId)

  const needsCard = canAutoOrder && autoOrder && !paymentMethodId
  const isUnchanged = !alertChanged && !autoChanged

  const handleSave = async () => {
    if (thresholdError || needsCard) return
    try {
      const tasks: Promise<unknown>[] = []
      if (alertChanged) {
        tasks.push(
          updateInventorySettingsAsync({
            tag_product_id: inventory.id,
            threshold: Number(threshold),
            threshold_type: tab,
            send_threshold_email: sendEmail,
          }),
        )
      }
      if (autoChanged) {
        tasks.push(
          updateAutoOrderAsync({
            tag_product_id: inventory.id,
            auto_order: autoOrder,
            auto_order_qty: autoOrder ? autoOrderVariant?.quantity ?? null : null,
            auto_order_payment_method_id: autoOrder ? paymentMethodId : null,
          }),
        )
      }
      await Promise.all(tasks)
      notification({ type: 'success', title: 'Inventory settings saved' })
      refetch()
      onClose()
    } catch {
      notification({ type: 'error', title: 'Could not save inventory settings' })
    }
  }

  const handleVariantChange = (item: { value: any }) => {
    const next = variants.find((variant: any) => variant.quantity == item.value)
    if (next) setAutoOrderVariant(next)
  }

  return (
    <Modal
      title="Inventory settings"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            disableElevation
            loading={isSaving}
            disabled={isUnchanged || Boolean(thresholdError) || needsCard || isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        </>
      }
    >
      <Stack spacing={5}>
        {/* ── Low-stock alert threshold (shared threshold both sections use) ── */}
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography variant="body2">Low-stock alert threshold</Typography>
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {/* "Spare capacity", not "available tags" (VERA-455): this threshold is
                  compared against `ordered − uncertified demand`, the same number the level
                  gauge plots — not against the tags physically on hand. */}
              Get alerted when your spare capacity falls below this level.
              {tab === 'percentage'
                ? ' Percentage is your spare capacity as a share of the products in stock yet to be certified.'
                : ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={3} alignItems="flex-start">
            <Input
              value={threshold}
              type="number"
              inputProps={{ min: 0, max: tab === 'percentage' ? 100 : undefined }}
              endAdornment={tab === 'percentage' ? '%' : 'tags'}
              error={Boolean(thresholdError)}
              helperText={thresholdError ?? undefined}
              onChange={(value) => setThreshold(value)}
              sx={{ width: 140 }}
            />
            <ToggleButton
              tab={tab}
              onChange={(next) => setTab(next as 'number' | 'percentage')}
              tabs={[
                { label: 'Number', value: 'number' },
                { label: 'Percentage', value: 'percentage' },
              ]}
            />
          </Stack>
          <Stack direction="row" spacing={3} alignItems="center">
            <Switch checked={sendEmail} onChange={() => setSendEmail(!sendEmail)} />
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Email me when tags drop below the threshold
            </Typography>
          </Stack>
        </Stack>

        <Divider />

        {/* ── Automatic reorder (money surface, gated by tags_auto_order) ── */}
        <Tooltip
          title={
            canAutoOrder
              ? ''
              : 'You need the “Enable auto-reorder” permission. Ask an admin to grant it.'
          }
          arrow
          disableHoverListener={canAutoOrder}
        >
          <Stack spacing={4} sx={{ opacity: canAutoOrder ? 1 : 0.5 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Switch
                checked={autoOrder}
                disabled={!canAutoOrder}
                onChange={() => setAutoOrder(!autoOrder)}
              />
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Automatically reorder tags when they drop below the threshold above
              </Typography>
            </Stack>

            <Collapse in={autoOrder && canAutoOrder}>
              <Stack spacing={4}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Typography variant="body3" color="text.secondary">
                    Quantity to automatically reorder
                  </Typography>
                  <MenuButton
                    items={variants.map((variant: any) => ({
                      label: variant.quantity.toString(),
                      value: variant.quantity,
                    }))}
                    onSelect={(item: { value: any }) => handleVariantChange(item)}
                    selected={
                      autoOrderVariant
                        ? {
                            label: autoOrderVariant.quantity.toString(),
                            value: autoOrderVariant.quantity,
                          }
                        : undefined
                    }
                  />
                </Stack>

                <Stack spacing={2}>
                  <Typography variant="body2">Payment method</Typography>
                  <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Auto-reorders are charged to this card.
                  </Typography>
                  {isLoadingCards ? (
                    <Typography variant="body3" color="text.secondary">
                      Loading cards…
                    </Typography>
                  ) : cards.length === 0 ? (
                    <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      No saved cards. Add a payment method in Billing settings to enable auto-reorder.
                    </Typography>
                  ) : (
                    // Dropdown of saved cards rendered like the Payment Methods page
                    // (brand image left, label, Default badge right). The selected
                    // row gets a right-aligned check; the default is preselected.
                    <MuiSelect
                      fullWidth
                      value={paymentMethodId ?? ''}
                      onChange={(e) => setPaymentMethodId(e.target.value as string)}
                      displayEmpty
                      renderValue={(val) => {
                        const c = cards.find((x) => x.id === val)
                        return c ? (
                          <PaymentCell paymentMethod={c} showDefault={c.isDefault} link={false} />
                        ) : (
                          <Typography variant="body3" color="text.secondary">
                            Select a card
                          </Typography>
                        )
                      }}
                      sx={{ '& .MuiSelect-select': { py: 1.5 } }}
                    >
                      {cards.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ width: '100%' }}
                          >
                            <PaymentCell paymentMethod={c} showDefault={c.isDefault} link={false} />
                            {paymentMethodId === c.id && (
                              <CheckIcon size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
                            )}
                          </Stack>
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  )}
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        </Tooltip>
      </Stack>
    </Modal>
  )
}
